import type { Ref } from 'vue'
import type { SchemaFormExpose, SchemaFormItem } from '@/components/FaSchemaForm/types'
import { nextTick, shallowRef, watch } from 'vue'

interface UseSchemaFormStateOptions<TForm extends Record<string, any>, TDetail = TForm> {
  form: TForm
  formRef: Ref<SchemaFormExpose<TForm> | undefined>
  getItems: () => SchemaFormItem<TForm>[]
  watchSource?: () => unknown
  getDetail?: () => Partial<TDetail> | undefined
}

export function useSchemaFormState<TForm extends Record<string, any>, TDetail = TForm>(options: UseSchemaFormStateOptions<TForm, TDetail>) {
  const items = shallowRef<SchemaFormItem<TForm>[]>([])

  function refreshItems() {
    items.value = options.getItems()
  }

  async function initialize(detail?: Partial<TDetail>) {
    options.formRef.value?.clearValidate()

    Object.keys(options.form).forEach((key) => {
      delete options.form[key as keyof TForm]
    })

    if (detail) {
      Object.assign(options.form, detail)
    }

    refreshItems()

    await nextTick()
    options.formRef.value?.syncInitialSnapshot?.()
  }

  if (options.watchSource) {
    watch(
      options.watchSource,
      () => initialize(options.getDetail?.()),
      { immediate: true },
    )
  }

  return {
    items,
    initialize,
    refreshItems,
  }
}
